import React, { useState } from "react";
import { useData } from "@/context/DataContext";
import { PageTitle, Fab, StickyTotal, RowActions, EmptyState } from "@/components/shared";
import DataDialog from "@/components/DataDialog";
import { formatRupiah, parseNumber } from "@/lib/format";
import { budgetSisa, isLunas } from "@/lib/compute";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function BudgetTable({ items, payments, onEdit, onDelete }) {
  if (items.length === 0) return <EmptyState />;
  return (
    <div className="rounded-xl border border-border">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Nama Budget</TableHead><TableHead className="text-right">Budget</TableHead>
          <TableHead className="text-right">Sisa Pelunasan</TableHead><TableHead>Status</TableHead>
          <TableHead className="text-right">Aksi</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((i) => {
            const lunas = isLunas(i, payments);
            return (
              <TableRow key={i.id} data-testid={`row-${i.id}`} className={lunas ? "bg-green-500/15 hover:bg-green-500/20" : ""}>
                <TableCell className="font-medium">{i.nama}</TableCell>
                <TableCell className="text-right">{formatRupiah(i.nominal)}</TableCell>
                <TableCell className="text-right">{formatRupiah(Math.max(0, budgetSisa(i, payments)))}</TableCell>
                <TableCell>{lunas ? <Badge className="bg-green-600">Lunas</Badge> : <Badge variant="outline">Belum</Badge>}</TableCell>
                <TableCell><RowActions id={i.id} onEdit={() => onEdit(i)} onDelete={() => onDelete(i.id)} /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Budget() {
  const { data, create, update, remove } = useData();
  const items = data.budget || [];
  const seserahan = data.seserahan || [];
  const payments = data.pembayaran || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const totalBudget = items.reduce((s, i) => s + parseNumber(i.nominal), 0);
  const totalSeserahan = seserahan.reduce((s, i) => s + parseNumber(i.estimasi), 0);
  const grandTotal = totalBudget + totalSeserahan;

  const fields = [
    { name: "agenda", label: "Agenda", type: "select", options: ["Lamaran", "Pernikahan"], required: true },
    { name: "nama", label: "Nama Budget", type: "text", required: true },
    { name: "nominal", label: "Nominal Budget", type: "currency", required: true },
  ];

  const onSave = async (payload) => {
    if (editing) { await update("budget", editing.id, payload); toast.success("Budget diperbarui"); }
    else { await create("budget", payload); toast.success("Budget ditambahkan"); }
  };

  const lamaran = items.filter((i) => i.agenda === "Lamaran");
  const pernikahan = items.filter((i) => i.agenda === "Pernikahan");

  return (
    <div>
      <PageTitle title="Budget" subtitle="Anggaran lamaran & pernikahan (termasuk seserahan)" />
      <StickyTotal testId="budget-total">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Budget</p>
        <p className="font-heading text-3xl font-semibold" data-testid="budget-total-value">{formatRupiah(grandTotal)}</p>
        <p className="mt-1 text-xs text-muted-foreground">Budget: {formatRupiah(totalBudget)} · Seserahan: {formatRupiah(totalSeserahan)}</p>
      </StickyTotal>

      <Tabs defaultValue="pernikahan">
        <TabsList data-testid="budget-tabs">
          <TabsTrigger value="lamaran" data-testid="tab-lamaran">Lamaran</TabsTrigger>
          <TabsTrigger value="pernikahan" data-testid="tab-pernikahan">Pernikahan</TabsTrigger>
        </TabsList>
        <TabsContent value="lamaran"><BudgetTable items={lamaran} payments={payments} onEdit={(i) => { setEditing(i); setOpen(true); }} onDelete={(id) => remove("budget", id)} /></TabsContent>
        <TabsContent value="pernikahan"><BudgetTable items={pernikahan} payments={payments} onEdit={(i) => { setEditing(i); setOpen(true); }} onDelete={(id) => remove("budget", id)} /></TabsContent>
      </Tabs>

      <Fab testId="fab-budget" onClick={() => { setEditing(null); setOpen(true); }} />
      <DataDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Budget" : "Tambah Budget"} fields={fields} initial={editing} onSave={onSave} />
    </div>
  );
}
